import React, { useState } from 'react';
import { X } from 'lucide-react';
import { galleryCategories, homestayInfo } from '../data/mockData';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';

const Gallery = () => {
  const [selectedImage, setSelectedImage] = useState(null);

  return (
    <div className="min-h-screen pt-20">
      {/* Header */}
      <section className="py-16 bg-gradient-to-br from-[#1F4D3A] to-[#2d6b52] text-white">
        <div className="container mx-auto px-4 text-center">
          <h1 className="text-4xl md:text-6xl font-bold mb-4">Gallery</h1>
          <p className="text-xl text-white/90 max-w-2xl mx-auto">
            Explore our rooms, views, and the natural beauty surrounding White Orchid Homestay
          </p>
        </div>
      </section>

      {/* Gallery Tabs */}
      <section className="py-16 bg-[#F7F5F0]">
        <div className="container mx-auto px-4">
          <Tabs defaultValue="rooms" className="max-w-7xl mx-auto">
            <TabsList className="w-full flex-wrap justify-center bg-white shadow-md mb-8 h-auto gap-2 p-2">
              {galleryCategories.map((category) => (
                <TabsTrigger
                  key={category.id}
                  value={category.id}
                  className="data-[state=active]:bg-[#1F4D3A] data-[state=active]:text-white px-4 py-2 rounded-md"
                >
                  {category.name}
                </TabsTrigger>
              ))}
            </TabsList>

            {galleryCategories.map((category) => (
              <TabsContent key={category.id} value={category.id} className="mt-0">
                <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
                  {category.images.map((image, index) => (
                    <div
                      key={index}
                      className="relative h-64 overflow-hidden rounded-lg group cursor-pointer shadow-md hover:shadow-xl transition-shadow"
                      onClick={() => setSelectedImage(image)}
                    >
                      <img
                        src={image}
                        alt={`${category.name} ${index + 1}`}
                        className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                      />
                      <div className="absolute inset-0 bg-black/0 group-hover:bg-black/20 transition-colors flex items-center justify-center">
                        <span className="text-white opacity-0 group-hover:opacity-100 transition-opacity text-sm font-medium">
                          Click to enlarge
                        </span>
                      </div>
                    </div>
                  ))}
                </div>
              </TabsContent>
            ))}
          </Tabs>
        </div>
      </section>

      {/* Lightbox Modal */}
      {selectedImage && (
        <div
          className="fixed inset-0 bg-black/90 z-50 flex items-center justify-center p-4"
          onClick={() => setSelectedImage(null)}
        >
          <button
            className="absolute top-4 right-4 text-white hover:text-gray-300 transition-colors"
            onClick={() => setSelectedImage(null)}
            aria-label="Close"
          >
            <X className="w-8 h-8" />
          </button>
          <img
            src={selectedImage}
            alt="Enlarged view"
            className="max-w-full max-h-full object-contain rounded-lg"
            onClick={(e) => e.stopPropagation()}
          />
        </div>
      )}

      {/* Featured Highlights */}
      <section className="py-16 bg-white">
        <div className="container mx-auto px-4">
          <div className="max-w-5xl mx-auto">
            <h2 className="text-4xl font-bold text-[#1F4D3A] text-center mb-12">
              Property Highlights
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
              <div className="text-center space-y-3">
                <div className="text-5xl font-bold text-[#D4A373]">4</div>
                <h3 className="text-xl font-semibold text-[#1F4D3A]">Spacious Rooms</h3>
                <p className="text-gray-600">Each 11 x 13 ft with unique views</p>
              </div>
              <div className="text-center space-y-3">
                <div className="text-5xl font-bold text-[#D4A373]">12</div>
                <h3 className="text-xl font-semibold text-[#1F4D3A]">Guest Capacity</h3>
                <p className="text-gray-600">Perfect for families and groups</p>
              </div>
              <div className="text-center space-y-3">
                <div className="text-5xl font-bold text-[#D4A373]">6</div>
                <h3 className="text-xl font-semibold text-[#1F4D3A]">Parking Spaces</h3>
                <p className="text-gray-600">Secure enclosed parking area</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Virtual Tour CTA */}
      <section className="py-16 bg-gradient-to-br from-[#A3B18A] to-[#1F4D3A] text-white">
        <div className="container mx-auto px-4 text-center">
          <h2 className="text-3xl md:text-4xl font-bold mb-4">
            Want to See More in Person?
          </h2>
          <p className="text-lg text-white/90 mb-8 max-w-2xl mx-auto">
            Book a visit and experience the beauty and comfort of White Orchid Homestay yourself
          </p>
          <button
            onClick={() => {
              const message = encodeURIComponent("Hello! I'd like to book a visit to White Orchid Homestay.");
              window.open(`https://wa.me/${homestayInfo.whatsapp.replace(/[^0-9]/g, '')}?text=${message}`, '_blank');
            }}
            className="bg-[#25D366] hover:bg-[#20BA5A] text-white px-8 py-4 rounded-lg text-lg font-semibold shadow-lg hover:shadow-xl transition-all"
          >
            Book Your Stay
          </button>
        </div>
      </section>
    </div>
  );
};

export default Gallery;
